import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Direct URL and API key values instead of environment variables to avoid loading issues
const supabaseUrl = 'https://ownhgsnatvvarqaovitz.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im93bmhnc25hdHZ2YXJxYW92aXR6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE2NjM2MjcsImV4cCI6MjA1NzIzOTYyN30.R4MUaGC6qTDUkpeNhOfJpZ6ejV1U6wTUFuhS1eF4wzI';

// Initialize Supabase client
const supabase = createClient(supabaseUrl, supabaseKey);

/**
 * Type definitions for spelling lists
 */
interface SpellingList {
  id: string;
  name: string;
  words: string[];
  created_at: string;
  user_id?: string;
  share_id?: string;
}

// Map to store the relationship between numeric IDs and UUIDs
const idMappings: { [numericId: string]: string } = (() => {
  try {
    const stored = localStorage.getItem('supabase_id_mappings');
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    console.error('Error loading ID mappings:', e);
    return {};
  }
})();

/**
 * Save ID mappings to localStorage
 */
const saveIdMappings = () => {
  try {
    localStorage.setItem('supabase_id_mappings', JSON.stringify(idMappings));
  } catch (e) {
    console.error('Error saving ID mappings:', e);
  }
};

/**
 * Get or create a UUID for a numeric ID
 * @param numericId The numeric ID
 * @returns The UUID
 */
const getUUID = (numericId: string | number): string => {
  const id = String(numericId);
  if (!idMappings[id]) {
    idMappings[id] = uuidv4();
    saveIdMappings();
  }
  return idMappings[id];
};

/**
 * Shares a spelling list by creating a share link
 * @param id The ID of the spelling list to share
 * @returns A promise that resolves to the share ID
 */
export const shareSpellingList = async (id: string): Promise<string> => {
  try {
    console.log('shareSpellingList called with id:', id);
    
    // Get the UUID for this ID
    const uuid = getUUID(id);
    console.log('Mapped numeric ID to UUID:', { numericId: id, uuid });
    
    // Generate a unique share ID
    const shareId = `share_${Date.now()}_${uuidv4().substring(0, 8)}`;
    console.log('Generated share ID:', shareId);
    
    // Try to upsert the list in Supabase (update if exists, insert if not)
    try {
      console.log('Attempting to upsert list in Supabase with share ID');
      
      // Get the list from localStorage to ensure we have all data for insert if needed
      const practices = JSON.parse(localStorage.getItem('practices') || '[]');
      const practice = practices.find((p: any) => String(p.id) === id);
      
      if (!practice) {
        console.error('List not found in localStorage');
        throw new Error('List not found');
      }
      
      console.log('Found list in localStorage:', practice);
      
      // Upsert the list into Supabase
      const { data, error } = await supabase
        .from('spelling_lists')
        .upsert([
          {
            id: uuid,
            name: practice.name,
            words: practice.words,
            created_at: practice.createdAt || new Date().toISOString(),
            share_id: shareId
          }
        ], 
        { 
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select();
      
      console.log('Supabase upsert response:', { data, error });
      
      if (!error) {
        // Successfully upserted in Supabase
        console.log('Successfully upserted list in Supabase with share ID');
        return shareId;
      } else {
        console.error('Error upserting list in Supabase:', error);
      }
    } catch (supabaseError) {
      console.error('Supabase operation failed with exception:', supabaseError);
      // Continue to fallback if Supabase operations fail
    }
    
    // Fallback to localStorage
    console.log('Using localStorage fallback for sharing');
    
    // Store the share ID in localStorage
    const sharedLists = JSON.parse(localStorage.getItem('sharedLists') || '{}');
    sharedLists[id] = {
      shareId,
      sharedAt: new Date().toISOString()
    };
    localStorage.setItem('sharedLists', JSON.stringify(sharedLists));
    console.log('Stored share ID in localStorage:', { id, shareId });
    
    return shareId;
  } catch (error) {
    console.error('Error sharing spelling list:', error);
    throw error;
  }
};

/**
 * Gets a shared spelling list by its share ID
 * @param shareId The share ID of the spelling list
 * @returns A promise that resolves to the spelling list or null if not found
 */
export const getSharedSpellingList = async (shareId: string): Promise<SpellingList | null> => {
  try {
    // Try to get from Supabase first
    try {
      const { data, error } = await supabase
        .from('spelling_lists')
        .select('*')
        .eq('share_id', shareId)
        .single();

      // If successful, return the data
      if (!error && data) {
        // Find the numeric ID that corresponds to this UUID
        let numericId = null;
        for (const [nId, uuid] of Object.entries(idMappings)) {
          if (uuid === data.id) {
            numericId = nId;
            break;
          }
        }
        
        return {
          id: numericId || data.id, // Use numeric ID if available, otherwise use UUID
          name: data.name,
          words: data.words,
          created_at: data.created_at,
          user_id: data.user_id,
          share_id: data.share_id
        };
      }
      
      // If there was an error or no data, return null
      if (error) {
        console.error('Supabase error retrieving shared list:', error);
      }
      return null;
    } catch (supabaseError) {
      console.error('Supabase operation failed:', supabaseError);
      return null;
    }
  } catch (error) {
    console.error('Error getting shared list:', error);
    return null;
  }
};

/**
 * Saves a spelling list to Supabase
 * @param name The name of the spelling list
 * @param words The words in the spelling list
 * @returns A promise that resolves to the created spelling list
 */
export const saveSpellingList = async (name: string, words: string[]): Promise<SpellingList> => {
  try {
    // Try to save to Supabase first
    try {
      const { data, error } = await supabase
        .from('spelling_lists')
        .insert([
          {
            name,
            words,
            created_at: new Date().toISOString(),
            user_id: null // Anonymous for now
          }
        ])
        .select()
        .single();

      if (!error && data) {
        // Store the relationship between the numeric ID and the UUID
        const numericId = String(Date.now()); // Generate a numeric ID
        idMappings[numericId] = data.id;
        saveIdMappings();

        return {
          id: numericId, // Return the numeric ID for local use
          name: data.name,
          words: data.words,
          created_at: data.created_at,
          user_id: data.user_id
        };
      }
    } catch (supabaseError) {
      console.log('Supabase operation failed, using localStorage fallback', supabaseError);
      // Continue to fallback if Supabase operations fail
    }

    // Fallback to localStorage
    console.log('Using localStorage fallback for saving');
    const id = String(Date.now());
    const newList = {
      id,
      name,
      words,
      createdAt: new Date().toISOString()
    };

    // Save to localStorage
    const practices = JSON.parse(localStorage.getItem('practices') || '[]');
    practices.push(newList);
    localStorage.setItem('practices', JSON.stringify(practices));

    return {
      id,
      name,
      words,
      created_at: newList.createdAt
    };
  } catch (error) {
    console.error('Error saving spelling list:', error);
    throw error;
  }
};

/**
 * Updates a spelling list in Supabase
 * @param id The ID of the spelling list to update
 * @param name The new name of the spelling list
 * @param words The new words in the spelling list
 * @returns A promise that resolves to the updated spelling list
 */
export const updateSpellingList = async (id: string, name: string, words: string[]): Promise<SpellingList> => {
  try {
    // Try to update in Supabase first
    try {
      const uuid = getUUID(id);
      const { data, error } = await supabase
        .from('spelling_lists')
        .update({
          name,
          words,
          updated_at: new Date().toISOString()
        })
        .eq('id', uuid)
        .select()
        .single();

      if (!error && data) {
        return {
          id, // Return the numeric ID for local use
          name: data.name,
          words: data.words,
          created_at: data.created_at,
          user_id: data.user_id
        };
      }
    } catch (supabaseError) {
      console.log('Supabase operation failed, using localStorage fallback', supabaseError);
      // Continue to fallback if Supabase operations fail
    }

    // Fallback to localStorage
    console.log('Using localStorage fallback for updating');
    const practices = JSON.parse(localStorage.getItem('practices') || '[]');
    const updatedPractices = practices.map((practice: any) => {
      if (String(practice.id) === id) {
        return {
          ...practice,
          name,
          words,
          updatedAt: new Date().toISOString()
        };
      }
      return practice;
    });
    localStorage.setItem('practices', JSON.stringify(updatedPractices));

    const updatedPractice = updatedPractices.find((p: any) => String(p.id) === id);
    return {
      id,
      name: updatedPractice.name,
      words: updatedPractice.words,
      created_at: updatedPractice.createdAt
    };
  } catch (error) {
    console.error('Error updating spelling list:', error);
    throw error;
  }
};

/**
 * Gets all spelling lists from Supabase
 * @returns A promise that resolves to an array of spelling lists
 */
export const getSpellingLists = async (): Promise<SpellingList[]> => {
  try {
    // Try to get from Supabase first
    try {
      const { data, error } = await supabase
        .from('spelling_lists')
        .select('*')
        .is('user_id', null); // Get only anonymous lists for now

      if (!error && data && data.length > 0) {
        // Map UUIDs to numeric IDs where possible
        return data.map(item => {
          // Find the numeric ID that corresponds to this UUID
          let numericId = null;
          for (const [nId, uuid] of Object.entries(idMappings)) {
            if (uuid === item.id) {
              numericId = nId;
              break;
            }
          }

          return {
            id: numericId || item.id, // Use numeric ID if available, otherwise use UUID
            name: item.name,
            words: item.words,
            created_at: item.created_at,
            user_id: item.user_id
          };
        });
      }
    } catch (supabaseError) {
      console.log('Supabase operation failed, using localStorage fallback', supabaseError);
      // Continue to fallback if Supabase operations fail
    }

    // Fallback to localStorage
    console.log('Using localStorage fallback for getting lists');
    const practices = JSON.parse(localStorage.getItem('practices') || '[]');
    return practices.map((practice: any) => ({
      id: String(practice.id),
      name: practice.name,
      words: practice.words,
      created_at: practice.createdAt
    }));
  } catch (error) {
    console.error('Error getting spelling lists:', error);
    return [];
  }
};

/**
 * Deletes a spelling list from Supabase
 * @param id The ID of the spelling list to delete
 * @returns A promise that resolves when the list is deleted
 */
export const deleteSpellingList = async (id: string): Promise<void> => {
  try {
    // Try to delete from Supabase first
    try {
      const uuid = getUUID(id);
      const { error } = await supabase
        .from('spelling_lists')
        .delete()
        .eq('id', uuid);

      if (!error) {
        // Successfully deleted from Supabase
        return;
      }
    } catch (supabaseError) {
      console.log('Supabase operation failed, using localStorage fallback', supabaseError);
      // Continue to fallback if Supabase operations fail
    }

    // Fallback to localStorage
    console.log('Using localStorage fallback for deleting');
    const practices = JSON.parse(localStorage.getItem('practices') || '[]');
    const updatedPractices = practices.filter((practice: any) => String(practice.id) !== id);
    localStorage.setItem('practices', JSON.stringify(updatedPractices));
  } catch (error) {
    console.error('Error deleting spelling list:', error);
    throw error;
  }
};
